/**
 * Upload-related type definitions for file attachments.
 */

/**
 * Represents a file attachment in the frontend.
 * Tracks the file through selection, upload, and reference stages.
 */
export interface FileAttachment {
  /** Unique identifier for this attachment (generated client-side) */
  id: string;
  /** The actual File object */
  file: File;
  /** Display name (from file.name) */
  name: string;
  /** File size in bytes */
  size: number;
  /** MIME type */
  mimeType: string;
  /** Category for display purposes */
  category: 'image' | 'document';
  /** Data URL for image previews, or undefined for documents */
  preview?: string;
  /** Upload progress (0-100) */
  uploadProgress: number;
  /** Current upload status */
  uploadStatus: 'pending' | 'uploading' | 'uploaded' | 'error';
  /** Error message if upload failed */
  errorMessage?: string;
  /** GCS URI after successful upload */
  gcsUri?: string;
  /** File ID from backend after successful upload */
  fileId?: string;
}

/**
 * Response from the upload endpoint for a single file.
 */
export interface UploadedFileResponse {
  file_id: string;
  gcs_uri: string;
  original_filename: string;
  content_type: string;
  size_bytes: number;
  file_type: 'image' | 'document';
}

/**
 * Response from the upload endpoint for multiple files.
 */
export interface MultiFileUploadResponse {
  files: UploadedFileResponse[];
  total_count: number;
  total_size_bytes: number;
}

/**
 * Response for listing project files.
 */
export interface FileListResponse {
  files: UploadedFileResponse[];
  total_count: number;
  total_size_bytes: number;
}

/**
 * Response containing a signed URL for file download.
 */
export interface SignedUrlResponse {
  url: string;
  expires_in_minutes: number;
}

/**
 * File attachment info stored in chat messages.
 */
export interface MessageAttachment {
  id: string;
  name: string;
  type: string;
  category: 'image' | 'document';
  preview?: string;
  fileId?: string;
}

/**
 * Configuration for file upload constraints.
 */
export interface UploadConfig {
  maxFileSize: number;
  maxFilesPerMessage: number;
  allowedImageTypes: string[];
  allowedDocumentTypes: string[];
}

/**
 * Default upload configuration.
 */
export const DEFAULT_UPLOAD_CONFIG: UploadConfig = {
  maxFileSize: 10 * 1024 * 1024, // 10 MB
  maxFilesPerMessage: 5,
  allowedImageTypes: ['image/png', 'image/jpeg', 'image/gif', 'image/webp'],
  allowedDocumentTypes: [
    'application/pdf',
    'text/plain',
    'text/markdown',
    'application/markdown', // Alternative MIME type browsers may use
    'text/x-markdown', // Another common markdown MIME variant
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  ],
};

/**
 * Get all allowed MIME types.
 */
export function getAllowedMimeTypes(
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): string[] {
  return [...config.allowedImageTypes, ...config.allowedDocumentTypes];
}

/**
 * Extension to MIME type mapping for fallback detection.
 * Used when browsers send application/octet-stream or empty type for known file types.
 */
const EXTENSION_TO_MIME_TYPE: Record<string, string> = {
  '.png': 'image/png',
  '.jpg': 'image/jpeg',
  '.jpeg': 'image/jpeg',
  '.gif': 'image/gif',
  '.webp': 'image/webp',
  '.pdf': 'application/pdf',
  '.txt': 'text/plain',
  '.md': 'text/markdown',
  '.markdown': 'text/markdown',
  '.docx':
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
};

/**
 * Detect MIME type from file extension.
 * Used as a fallback when browsers send empty or generic content types.
 */
export function detectMimeTypeFromExtension(filename: string): string | null {
  if (!filename) return null;

  const lastDot = filename.lastIndexOf('.');
  if (lastDot === -1) return null;

  const ext = filename.substring(lastDot).toLowerCase();
  return EXTENSION_TO_MIME_TYPE[ext] || null;
}

/**
 * Get the effective MIME type for a file, using extension-based detection as fallback.
 * This handles cases where browsers send application/octet-stream or empty type
 * for known file types like markdown files.
 */
export function getEffectiveMimeType(file: File): string {
  // If browser provides a specific MIME type, use it
  if (file.type && file.type !== 'application/octet-stream') {
    return file.type;
  }

  // Try to detect from extension
  const detected = detectMimeTypeFromExtension(file.name);
  if (detected) {
    return detected;
  }

  // Fall back to the browser's type or octet-stream
  return file.type || 'application/octet-stream';
}

/**
 * Check if a MIME type is an image type.
 */
export function isImageType(
  mimeType: string,
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): boolean {
  return config.allowedImageTypes.includes(mimeType);
}

/**
 * Check if a MIME type is a document type.
 */
export function isDocumentType(
  mimeType: string,
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): boolean {
  return config.allowedDocumentTypes.includes(mimeType);
}

/**
 * Get the category for a MIME type.
 */
export function getFileCategory(
  mimeType: string,
  config: UploadConfig = DEFAULT_UPLOAD_CONFIG
): 'image' | 'document' | 'unknown' {
  if (isImageType(mimeType, config)) return 'image';
  if (isDocumentType(mimeType, config)) return 'document';
  return 'unknown';
}
